import React, { useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Download } from 'lucide-react';
import { API } from '../api';
import EntityHeader, { EntityHeaderSkeleton, Facts } from '../ui/EntityHeader';
import EntityPage, { EntityLoadProblem, EntityTabs, TabTable, clearFiltersAction, useEntityTab } from '../ui/EntityPage';
import EntityLink from '../ui/EntityLink';
import EmptyState from '../ui/EmptyState';
import Button from '../ui/Button';
import { Spinner } from '../ui/Loading';
import { SeverityBadge } from '../ui/Badge';
import { formatCount, formatDate, teamLabel } from '../ui/format';
import useAsync from '../ui/useAsync';
import usePagedQuery from '../ui/usePagedQuery';
import useUrlFilters from '../ui/useUrlFilters';
import { ownershipPath } from '../ui/entityRoutes';
import EntityTickets, { fetchEntityTickets } from './EntityTickets';
import FindingsTab, { fetchFindingsCount } from './FindingsTab';
import useCycloneDx from './useCycloneDx';
import ExportButton from './ExportPanel';

const TAB_IDS = ['findings', 'vulnerabilities', 'packages', 'tickets'];
const muted = (text) => <span className="text-graphite">{text}</span>;
const dateCell = (value) => <span className="text-graphite tabular-nums">{formatDate(value) || '—'}</span>;

// Severity from the host's own counts; only Medium versus Low needs a lookup.
async function hostSeverity(host) {
  if (host.critical_vuln_count > 0) return 'Critical';
  if (host.high_vuln_count > 0) return 'High';
  if (!host.active_vuln_count) return null;
  const medium = await API.GET_VULNS_FOR_HOST({ hostId: host.id, severity: 'Medium', limit: 1 });
  return medium.total > 0 ? 'Medium' : 'Low';
}

async function loadOverview(host) {
  const settle = (promise) => promise.then((r) => r, () => null);
  const cveCount = (severity) => API.GET_VULNS_FOR_HOST({ hostId: host.id, severity, limit: 1, track: false });
  const [severity, vulns, criticalVulns, highVulns, findings, packages, tickets, ownership] = await Promise.all([
    settle(hostSeverity(host)),
    settle(cveCount()),
    settle(cveCount('Critical')),
    settle(cveCount('High')),
    settle(fetchFindingsCount({ hostId: host.id })),
    settle(API.GET_PACKAGES_FOR_HOST({ hostId: host.id, limit: 1 })),
    settle(fetchEntityTickets({ hostname: host.hostname }, { limit: 1 })),
    settle(API.GET_HOST_OWNERSHIPS({ kw: host.hostname, limit: 20 })),
  ]);
  return {
    severity,
    vulns: vulns?.total,
    criticalVulns: criticalVulns?.total,
    highVulns: highVulns?.total,
    findings: findings ?? undefined,
    packages: packages?.total,
    tickets: tickets?.total,
    admin: ownership?.data?.find((o) => o.id === host.id || o.hostname === host.hostname)?.host_admin ?? null,
  };
}

// One row per CVE with active findings on the host; package-level rows are in the Findings tab.
const VULN_COLUMNS = [
  { key: 'cve', header: 'CVE', render: (r) => <EntityLink kind="cve" cve={r.cve} /> },
  { key: 'severity', header: 'Severity', render: (r) => <SeverityBadge severity={r.severity} /> },
  {
    key: 'description',
    header: 'Description',
    grow: true,
    minWidth: 200,
    fullText: (r) => r.description,
    render: (r) => r.description || muted('No description'),
  },
  { key: 'category', header: 'Category', render: (r) => r.category || muted('—') },
  { key: 'published', header: 'Published', render: (r) => dateCell(r.published_at) },
  { key: 'findings', header: 'Findings', align: 'right', render: (r) => formatCount(r.finding_count) },
];

const PACKAGE_COLUMNS = [
  { key: 'package', header: 'Package', render: (p) => <EntityLink kind="package" id={p.id} name={p.name} /> },
  { key: 'version', header: 'Version', mono: true, render: (p) => p.version },
  { key: 'arch', header: 'Architecture', render: (p) => p.package_arch || muted('—') },
  { key: 'latest', header: 'Version status', render: (p) => (p.is_latest ? 'Latest' : muted('Outdated')) },
  { key: 'description', header: 'Description', grow: true, minWidth: 200, fullText: (p) => p.description, render: (p) => p.description || muted('No description') },
  { key: 'vulns', header: 'Vulnerabilities', align: 'right', render: (p) => (p.active_vuln_count ? formatCount(p.active_vuln_count) : muted('0')) },
  { key: 'first', header: 'First seen', render: (p) => dateCell(p.first_seen_at) },
  { key: 'last', header: 'Last seen', render: (p) => dateCell(p.last_seen_at) },
];

const VULN_FILTER_DEFAULTS = { q: '', severity: '' };
const VULN_FILTERS = [
  { key: 'q', type: 'search', label: 'CVE', placeholder: 'CVE ID', mono: true, width: 260 },
  {
    key: 'severity',
    type: 'select',
    label: 'Severity',
    options: [
      { value: '', label: 'All' },
      { value: 'Critical', label: 'Critical' },
      { value: 'High', label: 'High' },
      { value: 'Medium', label: 'Medium' },
      { value: 'Low', label: 'Low' },
    ],
  },
];

const PACKAGE_FILTER_DEFAULTS = { package: '', version: '' };
const PACKAGE_FILTERS = [
  { key: 'package', type: 'search', label: 'Package', placeholder: 'Name', mono: true, width: 240 },
  {
    key: 'version',
    type: 'select',
    label: 'Version',
    options: [
      { value: '', label: 'All' },
      { value: 'outdated', label: 'Outdated' },
      { value: 'latest', label: 'Latest' },
    ],
  },
];

function VulnerabilitiesTab({ host }) {
  const [filters, setFilters] = useUrlFilters(VULN_FILTER_DEFAULTS);
  const key = `${host.id}|${filters.q}|${filters.severity}`;
  const params = { hostId: host.id, kw: filters.q, severity: filters.severity };
  const fetchPage = useCallback(
    (paging) => API.GET_VULNS_FOR_HOST({ ...params, ...paging }),
    [key], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const query = usePagedQuery(fetchPage, key);
  return (
    <TabTable
      actions={<ExportButton query={{ endpoint: 'vulns_for_host', params }} />}
      label={`Vulnerabilities on ${host.hostname}`}
      filters={VULN_FILTERS}
      values={filters}
      onFiltersChange={setFilters}
      query={query}
      filtered={Boolean(filters.q || filters.severity)}
      columns={VULN_COLUMNS}
      rowKey={(r) => r.vulnerability_id}
      errorTitle="Vulnerabilities could not be loaded"
      empty={<EmptyState title="No vulnerabilities on this host">None of the packages on this host has an active CVE finding.</EmptyState>}
      filteredEmpty={(
        <EmptyState title="No vulnerabilities match these filters" action={clearFiltersAction(() => setFilters(VULN_FILTER_DEFAULTS))}>
          Search matches part of a CVE ID. To filter by package or fix availability, use the Findings tab.
        </EmptyState>
      )}
    />
  );
}

function PackagesTab({ host }) {
  const [filters, setFilters] = useUrlFilters(PACKAGE_FILTER_DEFAULTS);
  const key = `${host.id}|${filters.package}|${filters.version}`;
  const params = { hostId: host.id, kw: filters.package, outdated: filters.version };
  const fetchPage = useCallback(
    (paging) => API.GET_PACKAGES_FOR_HOST({ ...params, ...paging }),
    [key], // eslint-disable-line react-hooks/exhaustive-deps
  );
  const query = usePagedQuery(fetchPage, key);
  return (
    <TabTable
      actions={<ExportButton query={{ endpoint: 'packages_for_host', params }} />}
      label={`Packages on ${host.hostname}`}
      filters={PACKAGE_FILTERS}
      values={filters}
      onFiltersChange={setFilters}
      query={query}
      filtered={Boolean(filters.package || filters.version)}
      columns={PACKAGE_COLUMNS}
      rowKey={(p) => p.host_package_id}
      errorTitle="Packages could not be loaded"
      empty={<EmptyState title="No packages reported">This host hasn’t reported its installed packages yet, or it is no longer active.</EmptyState>}
      filteredEmpty={(
        <EmptyState title="No packages match these filters" action={clearFiltersAction(() => setFilters(PACKAGE_FILTER_DEFAULTS))}>
          The package filter matches part of the package name.
        </EmptyState>
      )}
    />
  );
}

function CycloneDxButton({ hostname }) {
  const cyclonedx = useCycloneDx();
  const busy = cyclonedx.busy === hostname;
  return (
    <Button onClick={() => cyclonedx.download(hostname)} disabled={busy}>
      {busy ? <Spinner /> : <Download size={15} strokeWidth={1.75} className="text-graphite" aria-hidden="true" />}
      Download CycloneDX
    </Button>
  );
}

const osSummary = (h) => {
  const os = [h.os_name, h.os_version].filter(Boolean).join(' ');
  const parts = [os && (h.codename ? `${os} (${h.codename})` : os), h.architecture].filter(Boolean);
  return parts.join(', ');
};

export default function HostPage() {
  const { id } = useParams();
  const [tab, setTab] = useEntityTab(TAB_IDS);
  const entity = useAsync(() => API.GET_HOST(id), id);
  const host = entity.data;
  const overview = useAsync(() => loadOverview(host), host ? host.id : null, { enabled: Boolean(host) });
  const counts = overview.data || {};
  const crumbs = [{ label: 'Hosts', to: '/hosts' }, { label: host?.hostname || 'Host' }];

  if (entity.error) {
    return <EntityLoadProblem crumbs={crumbs} noun="host" identifier={id} error={entity.error} onRetry={entity.retry} listPath="/hosts" listLabel="Go to Hosts" />;
  }

  const loadingValue = overview.loading ? muted('…') : null;
  const breakdown = [
    counts.criticalVulns && `${formatCount(counts.criticalVulns)} critical`,
    counts.highVulns && `${formatCount(counts.highVulns)} high`,
  ].filter(Boolean).join(', ');
  const vulnCount = counts.vulns === undefined ? null : (
    <>
      {formatCount(counts.vulns)}
      {breakdown && <span className="ml-1.5 font-normal text-graphite">({breakdown})</span>}
    </>
  );

  const facts = host && [
    {
      label: 'Highest severity',
      value: overview.loading ? loadingValue : (counts.severity ? <SeverityBadge severity={counts.severity} /> : <span className="font-normal text-graphite">None known</span>),
    },
    { label: 'Vulnerabilities', value: overview.loading ? loadingValue : vulnCount },
    { label: 'Packages', value: formatCount(host.active_package_count) },
    { label: 'IP address', value: host.ip, mono: true },
    { label: 'State', value: host.state ? host.state.charAt(0).toUpperCase() + host.state.slice(1) : null },
    { label: 'First seen', value: formatDate(host.first_seen_at) },
    { label: 'Last seen', value: formatDate(host.last_seen_at) },
  ];

  const ownership = host && [
    { label: 'Team', value: teamLabel(host.team_flow) },
    { label: 'Project', value: host.team_project },
    { label: 'Region', value: host.region },
    { label: 'Admin', value: overview.loading ? loadingValue : counts.admin },
  ];

  return (
    <EntityPage>
      {!host ? (
        <EntityHeaderSkeleton crumbs={crumbs} />
      ) : (
        <EntityHeader
          crumbs={crumbs}
          identifier={host.hostname}
          severity={counts.severity}
          summary={osSummary(host)}
          facts={facts}
          actions={<CycloneDxButton hostname={host.hostname} />}
        >
          <div className="mt-3 flex flex-wrap items-end gap-x-7 gap-y-2">
            <Facts items={ownership} />
            <Link to={ownershipPath(host.hostname)} className="text-small text-signal hover:underline">
              Edit ownership
            </Link>
          </div>
        </EntityHeader>
      )}
      {host && (
        <EntityTabs
          label={`${host.hostname} details`}
          value={tab}
          onChange={setTab}
          tabs={[
            {
              id: 'findings',
              label: 'Findings',
              count: counts.findings,
              render: () => <FindingsTab scope={{ hostId: host.id }} hide={['host']} label={`Findings on ${host.hostname}`} />,
            },
            { id: 'vulnerabilities', label: 'Vulnerabilities', count: counts.vulns, render: () => <VulnerabilitiesTab host={host} /> },
            { id: 'packages', label: 'Packages', count: counts.packages, render: () => <PackagesTab host={host} /> },
            {
              id: 'tickets',
              label: 'Tickets',
              count: counts.tickets,
              render: () => (
                <EntityTickets
                  filter={{ hostname: host.hostname }}
                  emptyTitle={`No tickets for ${host.hostname}`}
                  emptyText="Tickets that include this host appear here. Create them from the Tickets page."
                  emptyAction={<Button to="/tickets">Go to Tickets</Button>}
                />
              ),
            },
          ]}
        />
      )}
    </EntityPage>
  );
}
